import { useCallback, useEffect, useRef, useState } from 'react';
import { notification } from '@spark/ui-components/utils/notification';
import {
  deleteOuvidoria,
  getMotivosOuvidoria,
  getOuvidorias,
  getTagsOuvidoria,
} from '@spark/services';
import {
  buildOptionsFromItems,
  formatBooleanLabel,
  formatDateTime,
  formatTagsLabel,
} from '../../shared/constants';

const filtrosIniciais = {
  id: '',
  motivoId: '',
  relato: '',
  desejaAcompanhar: '',
  relatadoLiderStatus: '',
  statusAtual: '',
  dataCriacaoInicial: '',
  dataCriacaoFinal: '',
  protocolo: '',
  tagId: '',
};

export const useOuvidoriaListagem = () => {
  const [registros, setRegistros] = useState([]);
  const [loading, setLoading] = useState(true);
  const [filterLoading, setFilterLoading] = useState(false);
  const [paginaAtual, setPaginaAtual] = useState(1);
  const [quantidadePorPagina, setQuantidadePorPagina] = useState(5);
  const [totalRegistros, setTotalRegistros] = useState(0);
  const [filtrosUI, setFiltrosUI] = useState(filtrosIniciais);
  const [motivoOptions, setMotivoOptions] = useState([]);
  const [tagOptions, setTagOptions] = useState([]);
  const hasLoadedOnce = useRef(false);

  const loadFilterOptions = useCallback(async () => {
    try {
      const [motivosResponse, tagsResponse] = await Promise.all([
        getMotivosOuvidoria({ pagina: 1, limitePorPagina: 200 }),
        getTagsOuvidoria({ pagina: 1, limitePorPagina: 200 }),
      ]);

      setMotivoOptions(buildOptionsFromItems(motivosResponse?.itens || [], 'id', 'nomeMotivo'));
      setTagOptions(buildOptionsFromItems(tagsResponse?.itens || [], 'id', 'nomeTag'));
    } catch (error) {
      console.error('Erro ao carregar opcoes de filtro da ouvidoria:', error);
      setMotivoOptions([]);
      setTagOptions([]);
    }
  }, []);

  const fetchData = useCallback(async () => {
    const isInitialLoad = !hasLoadedOnce.current;

    try {
      if (isInitialLoad) {
        setLoading(true);
      } else {
        setFilterLoading(true);
      }

      const response = await getOuvidorias({
        ...filtrosUI,
        pagina: paginaAtual,
        limitePorPagina: quantidadePorPagina,
      });

      const itens = Array.isArray(response?.itens) ? response.itens : [];
      const normalizados = itens.map(item => ({
        id: item.id,
        protocolo: item.protocolo || '',
        motivoId: item.motivoId,
        motivoNome: item?.motivo?.nomeMotivo || '',
        relato: item.relato || '',
        desejaAcompanhar: item.desejaAcompanhar,
        desejaAcompanharLabel: formatBooleanLabel(item.desejaAcompanhar),
        relatadoLiderStatus: item.relatadoLiderStatus || '',
        statusAtual: item.statusAtual || '',
        dataCriacao: item.dataCriacao || '',
        dataCriacaoFormatada: formatDateTime(item.dataCriacao),
        tags: Array.isArray(item.tags) ? item.tags : [],
        tagsLabel: formatTagsLabel(item.tags),
      }));

      const ordenados = [...normalizados].sort((a, b) => {
        const dataA = a?.dataCriacao ? new Date(a.dataCriacao).getTime() : 0;
        const dataB = b?.dataCriacao ? new Date(b.dataCriacao).getTime() : 0;
        if (dataA !== dataB) return dataB - dataA;
        return Number(b?.id || 0) - Number(a?.id || 0);
      });

      setRegistros(ordenados);
      setTotalRegistros(Number(response?.paginacao?.totalItens || 0));
    } catch (error) {
      console.error('Erro ao buscar registros de ouvidoria:', error);
      setRegistros([]);
      setTotalRegistros(0);
    } finally {
      hasLoadedOnce.current = true;
      setLoading(false);
      setFilterLoading(false);
    }
  }, [filtrosUI, paginaAtual, quantidadePorPagina]);

  useEffect(() => {
    loadFilterOptions();
  }, [loadFilterOptions]);

  useEffect(() => {
    fetchData();
  }, [fetchData]);

  const handleFilterApply = useCallback(newFilters => {
    setPaginaAtual(1);
    setFiltrosUI({
      id: newFilters.id || '',
      motivoId: newFilters.motivoId || '',
      relato: newFilters.relato || '',
      desejaAcompanhar: newFilters.desejaAcompanhar || '',
      relatadoLiderStatus: newFilters.relatadoLiderStatus || '',
      statusAtual: newFilters.statusAtual || '',
      dataCriacaoInicial: newFilters.dataCriacaoInicial || '',
      dataCriacaoFinal: newFilters.dataCriacaoFinal || '',
      protocolo: newFilters.protocolo || '',
      tagId: newFilters.tagId || '',
    });
  }, []);

  const handleLimparFiltros = useCallback(() => {
    setPaginaAtual(1);
    setFiltrosUI(filtrosIniciais);
  }, []);

  const handleExcluir = useCallback(
    async id => {
      if (!window.confirm('Tem certeza que deseja excluir este registro de ouvidoria?')) {
        return;
      }

      try {
        await deleteOuvidoria(id);
        notification.success('Registro de ouvidoria excluido com sucesso!');
        await fetchData();
      } catch (error) {
        console.error('Erro ao excluir registro de ouvidoria:', error);
      }
    },
    [fetchData]
  );

  return {
    data: registros,
    loading,
    filterLoading,
    totalRegistros,
    paginaAtual,
    quantidadePorPagina,
    filtrosUI,
    motivoOptions,
    tagOptions,
    handleFilterApply,
    handleLimparFiltros,
    handleExcluir,
    setPaginaAtual,
    setQuantidadePorPagina,
    refetch: fetchData,
  };
};
